import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import jobService from '../../services/jobService';

const ViewJobDialog = ({ isOpen, onClose, jobId, onEdit, onDelete, userRole }) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canManageJobs = ['admin', 'hr_manager', 'recruiter'].includes(userRole);
  const canDeleteJobs = ['admin', 'hr_manager'].includes(userRole);

  useEffect(() => {
    if (isOpen && jobId) {
      loadJobDetails();
    }
  }, [isOpen, jobId]);

  const loadJobDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await jobService.getJobById(jobId);
      setJob(response.job);
    } catch (error) {
      setError(error.message || 'Failed to load job details');
      console.error('Error loading job details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatSalary = (min, max, currency) => {
    if (!min && !max) return 'Not specified';
    if (min && max) return `${currency} ${min.toLocaleString()} - ${max.toLocaleString()}`;
    if (min) return `${currency} ${min.toLocaleString()}+`;
    return `Up to ${currency} ${max.toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'paused': return 'bg-blue-100 text-blue-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleEdit = () => {
    onEdit && onEdit(job);
    onClose();
  };

  const handleDelete = () => {
    onDelete && onDelete(job.id);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Job Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader2" size={48} className="animate-spin text-gray-400" />
              <span className="ml-3 text-gray-600">Loading job details...</span>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <Icon name="AlertCircle" size={48} className="mx-auto text-red-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Job</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={loadJobDetails} variant="outline">
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Try Again
              </Button>
            </div>
          ) : job ? (
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{job.title}</h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span className="flex items-center">
                        <Icon name="Building" size={16} className="mr-1" />
                        {job.department}
                      </span>
                      <span className="flex items-center">
                        <Icon name="MapPin" size={16} className="mr-1" />
                        {job.location}
                      </span>
                      <span className="flex items-center">
                        <Icon name="Clock" size={16} className="mr-1" />
                        {job.employment_type}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(job.status)}`}>
                      {job.status}
                    </span>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getPriorityColor(job.priority)}`}>
                      {job.priority} priority
                    </span>
                  </div>
                </div>

                {job.remote_work_allowed && (
                  <div className="inline-flex items-center bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm">
                    <Icon name="Wifi" size={14} className="mr-1" />
                    Remote work allowed
                  </div>
                )}
              </div>

              {/* Salary Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Compensation</h4>
                <p className="text-lg font-semibold text-green-600">
                  {formatSalary(job.salary_range_min, job.salary_range_max, job.currency)}
                </p>
              </div>

              {/* Job Description */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Job Description</h4>
                <div className="prose max-w-none text-gray-700">
                  <p className="whitespace-pre-wrap">{job.description}</p>
                </div>
              </div>

              {/* Requirements & Responsibilities */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Requirements</h4>
                  {job.requirements && job.requirements.length > 0 ? (
                    <ul className="space-y-2">
                      {job.requirements.map((req, index) => (
                        <li key={index} className="flex items-start">
                          <Icon name="Check" size={16} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                          <span className="text-gray-700">{req}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 italic">No requirements specified</p>
                  )}
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Responsibilities</h4>
                  {job.responsibilities && job.responsibilities.length > 0 ? (
                    <ul className="space-y-2">
                      {job.responsibilities.map((resp, index) => (
                        <li key={index} className="flex items-start">
                          <Icon name="ArrowRight" size={16} className="text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                          <span className="text-gray-700">{resp}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 italic">No responsibilities specified</p>
                  )}
                </div>
              </div>

              {/* Skills */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Required Skills</h4>
                  {job.skills_required && job.skills_required.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {job.skills_required.map((skill, index) => (
                        <span key={index} className="bg-red-50 text-red-700 px-3 py-1 rounded-full text-sm">
                          {skill}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No required skills specified</p>
                  )}
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Preferred Skills</h4>
                  {job.skills_preferred && job.skills_preferred.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {job.skills_preferred.map((skill, index) => (
                        <span key={index} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm">
                          {skill}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No preferred skills specified</p>
                  )}
                </div>
              </div>

              {/* Benefits */}
              {job.benefits && job.benefits.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Benefits</h4>
                  <div className="flex flex-wrap gap-2">
                    {job.benefits.map((benefit, index) => (
                      <span key={index} className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm">
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900">Job Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Experience Level:</span>
                      <span className="text-gray-900 capitalize">{job.experience_level}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Travel Required:</span>
                      <span className="text-gray-900 capitalize">{job.travel_required || 'None'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Education:</span>
                      <span className="text-gray-900 capitalize">{job.education_required || 'Not specified'}</span>
                    </div>
                    {job.team_size && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Team Size:</span>
                        <span className="text-gray-900">{job.team_size} people</span>
                      </div>
                    )}
                    {job.reporting_manager && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Reports To:</span>
                        <span className="text-gray-900">{job.reporting_manager}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900">Timeline</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Created:</span>
                      <span className="text-gray-900">{formatDate(job.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Updated:</span>
                      <span className="text-gray-900">{formatDate(job.updated_at)}</span>
                    </div>
                    {job.published_at && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Published:</span>
                        <span className="text-gray-900">{formatDate(job.published_at)}</span>
                      </div>
                    )}
                    {job.application_deadline && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Application Deadline:</span>
                        <span className="text-gray-900">{formatDate(job.application_deadline)}</span>
                      </div>
                    )}
                    {job.start_date && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Start Date:</span>
                        <span className="text-gray-900">{formatDate(job.start_date)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Interview Process */}
              {job.interview_process && job.interview_process.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Interview Process</h4>
                  <ol className="space-y-2">
                    {job.interview_process.map((step, index) => (
                      <li key={index} className="flex items-start">
                        <span className="bg-blue-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          {index + 1}
                        </span>
                        <span className="text-gray-700">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Certifications */}
              {job.certifications_required && job.certifications_required.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Required Certifications</h4>
                  <div className="flex flex-wrap gap-2">
                    {job.certifications_required.map((cert, index) => (
                      <span key={index} className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm">
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {job && (
          <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-500">
              Job ID: {job.id}
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              
              {canManageJobs && (
                <Button onClick={handleEdit} className="bg-blue-600 hover:bg-blue-700">
                  <Icon name="Edit" size={16} className="mr-2" />
                  Edit Job
                </Button>
              )}
              
              {canDeleteJobs && (
                <Button 
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Icon name="Trash2" size={16} className="mr-2" />
                  Delete Job
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewJobDialog;